import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import LoyaltyAccountStatusSummary from 'loyalty/components/LoyaltyAccountStatusSummary'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import AccountDrawerBenefitHighlightItem from './AccountDrawerBenefitHighlightItem'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

const TIER_BENEFITS: Record<App.LoyaltyTier, Array<App.LoyaltyBenefit['code']>> = {
  bronze: [],
  silver: ['hotel_upgrade', 'lounge_pass', 'discounted_insurance'],
  gold: ['hotel_upgrade', 'airport_transfer', 'lounge_pass'],
  platinum: ['airport_transfer', 'hotel_upgrade', 'lounge_pass'],
}

const BENEFIT_CONFIG: Utils.PartialRecord<App.LoyaltyBenefit['code'], {
  title: string;
  icon: React.ReactElement;
}> = {
  lounge_pass: {
    title: 'Free airport lounge passes',
    icon: <LineChairWithAirplaneIcon />,
  },
  airport_transfer: {
    title: 'Free airport chauffeur service',
    icon: <LineTaxiIcon />,
  },
  hotel_upgrade: {
    title: 'Free room or package upgrade',
    icon: <LineCrownIcon />,
  },
  discounted_insurance: {
    title: 'Discounted travel insurance',
    icon: <LineShieldCheckIcon />,
  },
}

interface BenefitConfigItem {
  code: App.LoyaltyBenefit['code'];
  displayInfo: {
    title: string;
    icon: React.ReactElement;
  };
}

function getTierBenefitDisplayInfo(tier: App.LoyaltyTier): Array<BenefitConfigItem> {
  const tierBenefits = TIER_BENEFITS[tier]
  const displayInfoItems = tierBenefits.map(code => {
    const displayInfo = BENEFIT_CONFIG[code]
    return displayInfo ? { code, displayInfo } : undefined
  })
  return nonNullable(displayInfoItems)
}

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTile({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const benefits = loyaltyAccountData.benefits
  const points = loyaltyAccountData.points

  const benefitsMap = arrayToObject(benefits, benefit => benefit.code)
  const tierBenefitsDisplayInfo = getTierBenefitDisplayInfo(tier)

  const validBenefitsDisplayInfo = nonNullable(
    tierBenefitsDisplayInfo.map(displayInfo =>
      benefitsMap[displayInfo.code] ? displayInfo : undefined,
    ),
  )

  const loyaltyUpgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)
  const BENEFIT_PROMOTION: Utils.PartialRecord<App.LoyaltyBenefit['code'], App.LabFeatureFlagResult> = useMemo(() => ({
    hotel_upgrade: loyaltyUpgradesPromo
  }), [loyaltyUpgradesPromo])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      to={`/${LOYALTY_LANDING_PAGE}`}
      onClick={onLinkClick}
      className={cn(`tier-${tier}`)}
    >
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <LoyaltyProgramNameLogo width={72} />
            {tier && <BodyText variant="medium" format="uppercase">{getLoyaltyTierDisplayName(tier)}</BodyText>}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
            <Caption variant="large">Membership {loyaltyAccountData.accountNumber}
            </Caption>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Heading variant="heading6">
                <FormatDecimal value={points} />
              </Heading>
              <Caption variant="medium" weight="normal">pts</Caption>
            </Group>
          </Group>
        </VerticalSpacer>
        <VerticalSpacer gap={20}>
          <LoyaltyAccountStatusSummary />
          {validBenefitsDisplayInfo && validBenefitsDisplayInfo.length > 0 && <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">Your top benefits</BodyText>
            {validBenefitsDisplayInfo.map(({ code, displayInfo }, index) => (
              <React.Fragment key={code}>
                {index > 0 && <Divider kind="secondary" />}
                <AccountDrawerBenefitHighlightItem
                  benefit={benefitsMap[code]}
                  tier={tier}
                  title={displayInfo.title}
                  icon={displayInfo.icon}
                  promotion={BENEFIT_PROMOTION[code]}
                />
              </React.Fragment>
            ))}
          </VerticalSpacer>}
          <Group direction="horizontal" horizontalAlign="space-between">
            {loyaltyAccountData.statusReviewDate && <Caption variant="medium">Review date{' '}
              <FormatDateTime value={loyaltyAccountData.statusReviewDate} format="dmy-casual-short" />
            </Caption>}
            <TextLink size="small">
              View membership
            </TextLink>
          </Group>
        </VerticalSpacer>
      </VerticalSpacer>
    </Container>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTile)
